import { useState, useEffect, useRef, useCallback } from 'react';

export interface WebSocketMessage {
  type: string;
  message: string;
  timestamp: string;
  active_connections?: number;
  original_message?: unknown;
}

export const useWebSocket = (url: string) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  // Refs so cleanup/disconnect always see the live socket (state is stale in closures)
  const socketRef = useRef<WebSocket | null>(null);
  const intentionalCloseRef = useRef(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [readyState, setReadyState] = useState<number>(WebSocket.CONNECTING);
  const [messageHistory, setMessageHistory] = useState<WebSocketMessage[]>([]);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectInterval = useRef(1000); // Start with 1 second

  // Check if WebSocket is enabled via environment variable
  const isWebSocketEnabled = process.env.REACT_APP_WEBSOCKET_ENABLED !== 'false';

  const connect = useCallback(() => {
    // Skip connection if WebSocket is disabled
    if (!isWebSocketEnabled) {
      setReadyState(WebSocket.CLOSED);
      return;
    }

    intentionalCloseRef.current = false;

    try {
      const ws = new WebSocket(url);

      ws.onopen = () => {
        // console.log('WebSocket connected');
        setReadyState(WebSocket.OPEN);
        reconnectAttempts.current = 0;
        reconnectInterval.current = 1000; // Reset reconnect interval
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);
          setMessageHistory((prev) => [...prev.slice(-19), message]); // Keep last 20 messages
        } catch (error) {
          // console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        // console.log('WebSocket disconnected', event.code, event.reason);
        // Ignore close events from sockets that were replaced or intentionally closed
        if (socketRef.current !== ws) return;
        socketRef.current = null;
        setReadyState(WebSocket.CLOSED);
        setSocket(null);

        // Don't reconnect on intentional closes, clean closes (1000) or policy violations (1008)
        const shouldNotReconnect =
          intentionalCloseRef.current || event.code === 1000 || event.code === 1008;

        // Auto-reconnect with exponential backoff
        if (!shouldNotReconnect && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (intentionalCloseRef.current) return;
            // console.log(`Reconnecting... Attempt ${reconnectAttempts.current + 1}`);
            reconnectAttempts.current++;
            reconnectInterval.current = Math.min(reconnectInterval.current * 2, 30000); // Max 30 seconds
            connect();
          }, reconnectInterval.current);
        } else {
          // console.log('Max reconnection attempts reached or clean close');
        }
      };

      ws.onerror = (_error) => {
        // console.error('WebSocket error:', _error);
        setReadyState(WebSocket.CLOSED);
        // Close the socket if it's in an error state
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          try {
            ws.close(1000, 'Client closing due to error');
          } catch (closeError) {
            // Ignore close errors
          }
        }
      };

      socketRef.current = ws;
      setSocket(ws);
      setReadyState(WebSocket.CONNECTING);
    } catch (error) {
      // console.error('Error creating WebSocket connection:', error);
      setReadyState(WebSocket.CLOSED);
    }
  }, [url, isWebSocketEnabled]);

  // Close the live socket without triggering auto-reconnect
  const closeSocket = useCallback((reason: string) => {
    intentionalCloseRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    const ws = socketRef.current;
    socketRef.current = null;
    if (ws) {
      try {
        // Only close if not already closed/closing
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, reason);
        }
      } catch (error) {
        // Ignore errors during disconnect
      }
    }
  }, []);

  const disconnect = useCallback(() => {
    closeSocket('Client disconnect');
    setSocket(null);
    setReadyState(WebSocket.CLOSED);
  }, [closeSocket]);

  const sendMessage = useCallback((message: unknown) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    } else {
      // console.warn('WebSocket is not connected');
    }
  }, []);

  const clearHistory = useCallback(() => {
    setMessageHistory([]);
    setLastMessage(null);
  }, []);

  useEffect(() => {
    connect();

    return () => {
      closeSocket('Component unmounting');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array for initial connection only

  const connectionStatus = (() => {
    const statusMap: { [key: number]: string } = {
      [WebSocket.CONNECTING]: 'Connecting',
      [WebSocket.OPEN]: 'Connected',
      [WebSocket.CLOSING]: 'Closing',
      [WebSocket.CLOSED]: 'Disconnected',
    };
    return statusMap[readyState] || 'Unknown';
  })();

  return {
    socket,
    lastMessage,
    readyState,
    messageHistory,
    sendMessage,
    connect,
    disconnect,
    clearHistory,
    connectionStatus,
    isConnected: readyState === WebSocket.OPEN,
  };
};
